/**
 * InfoRequestPage — public, no-login page a client opens from the emailed link to answer
 * their adviser's follow-up questions (and, when asked, approve their resume or request
 * changes).
 *
 * Flow: GET /info-requests/public/:requestCode -> render the questions (text, long text,
 * date, yes/no, choice, number) + optional resume review -> POST .../submit (one time).
 * The server stores the answers, posts them into the client's conversation and notifies
 * the adviser; expiry and one-time submission are enforced server-side.
 *
 * Public route (see App.js standalone block). Hits maven-platform-server directly with
 * plain axios (no Auth0) — the link itself is the credential.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import LogoLoader from '../LogoLoader';
import styled from 'styled-components';

const MAVEN_API_BASE = process.env.REACT_APP_MAVEN_SERVER || 'https://sea-lion-app-7wbpx.ondigitalocean.app';

const T = {
  en: {
    loading: 'Loading…', from: 'From', required: 'Required', yes: 'Yes', no: 'No', choose: 'Choose…',
    reviewTitle: 'Your resume', reviewHelp: 'Please read it carefully — is everything correct?',
    open: 'Open the resume', approve: 'Everything is correct', changes: 'Something needs to change',
    changesPh: 'Tell us what is wrong or missing (dates, job titles, duties…)',
    submit: 'Send my answers', sending: 'Sending…', missing: 'Please answer the questions marked in red.',
    doneTitle: 'Thank you!', doneText: 'Your answers were sent to your adviser.',
    already: 'Your answers were already received — thank you!', error: 'Something went wrong — please try again.',
    validUntil: 'This link is valid until',
    optional: 'Answer what you can — you can skip any question not marked *.',
    atLeastOne: 'Please answer at least one question.',
  },
  fr: {
    loading: 'Chargement…', from: 'De la part de', required: 'Obligatoire', yes: 'Oui', no: 'Non', choose: 'Choisir…',
    reviewTitle: 'Votre CV', reviewHelp: 'Veuillez le lire attentivement — tout est-il exact ?',
    open: 'Ouvrir le CV', approve: 'Tout est exact', changes: 'Quelque chose doit être modifié',
    changesPh: 'Indiquez ce qui est inexact ou manquant (dates, titres de poste, tâches…)',
    submit: 'Envoyer mes réponses', sending: 'Envoi…', missing: 'Veuillez répondre aux questions indiquées en rouge.',
    doneTitle: 'Merci !', doneText: 'Vos réponses ont été transmises à votre conseiller.',
    already: 'Vos réponses ont déjà été reçues — merci !', error: 'Une erreur est survenue — veuillez réessayer.',
    validUntil: "Ce lien est valide jusqu'au",
    optional: "Répondez à ce que vous pouvez — vous pouvez passer toute question sans *.",
    atLeastOne: 'Veuillez répondre à au moins une question.',
  },
};

const Page = styled.div`
  min-height: 100vh; width: 100%; display: flex; flex-direction: column; align-items: center;
  background: linear-gradient(135deg, #f9fbff 0%, #ffffff 100%); color: #33445e;
  padding: 24px 16px 40px; box-sizing: border-box;
`;
const Card = styled.div`
  width: 100%; max-width: 720px; background: rgba(244, 245, 249, 0.92); border: 1px solid rgba(120,140,180,0.18);
  border-radius: 16px; padding: 22px 22px; margin-top: 16px; box-sizing: border-box;
`;
const Title = styled.h1`font-size: 22px; margin: 0 0 6px; color: #192947; line-height: 1.3;`;
const Muted = styled.p`margin: 0; color: #65748d; font-size: 14px; line-height: 1.55; white-space: pre-wrap;`;
const Q = styled.div`
  margin-top: 18px; padding: 14px; border-radius: 12px;
  border: 1px solid ${p => (p.$err ? 'rgba(229, 72, 77, 0.6)' : 'rgba(120,140,180,0.15)')};
  background: rgba(25, 41, 71, 0.105);
`;
const QLabel = styled.label`display: block; font-size: 15.5px; font-weight: 600; color: #192947; line-height: 1.4;`;
const Help = styled.div`font-size: 13px; color: #8b93a7; margin-top: 4px;`;
const inputCss = `
  width: 100%; box-sizing: border-box; margin-top: 10px; background: rgba(25, 41, 71, 0.24);
  border: 1px solid rgba(120,140,180,0.25); border-radius: 10px; padding: 12px; color: #192947;
  font-size: 16px; outline: none; font-family: inherit;
  &:focus { border-color: rgba(136, 93, 232, 0.7); }
`;
const Input = styled.input`${inputCss}`;
const Area = styled.textarea`${inputCss} min-height: 110px; resize: vertical; line-height: 1.45;`;
const Sel = styled.select`${inputCss} & option { background: #f3f5f9; }`;
const Choice = styled.button`
  flex: 1; margin-top: 10px; padding: 12px; border-radius: 10px; font-size: 15px; font-weight: 600; cursor: pointer;
  border: 1px solid ${p => (p.$on ? 'rgba(136, 93, 232, 0.8)' : 'rgba(120,140,180,0.25)')};
  background: ${p => (p.$on ? 'rgba(136, 93, 232, 0.22)' : 'rgba(25, 41, 71, 0.18)')}; color: #192947;
`;
const Button = styled.button`
  width: 100%; max-width: 720px; margin-top: 20px; padding: 16px; border: none; border-radius: 12px;
  font-weight: 700; font-size: 17px; cursor: pointer; color: #fff;
  background: linear-gradient(135deg, #4b52e0 0%, #7955cd 100%); opacity: ${p => (p.disabled ? 0.6 : 1)};
`;
const Frame = styled.iframe`width: 100%; height: 60vh; border: none; border-radius: 10px; background: #fff; margin-top: 12px;`;
const LinkBtn = styled.a`
  display: inline-block; margin-top: 10px; color: #5874be; font-weight: 600; font-size: 15px;
`;
const Center = styled.div`
  min-height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 14px;
  background: linear-gradient(135deg,#f9fbff,#ffffff); color: #33445e; padding: 24px; text-align: center;
`;
const ErrorText = styled.div`color: #c53030; font-size: 14px; margin-top: 12px; text-align: center;`;

export default function InfoRequestPage() {
  const { requestCode } = useParams();
  const url = `${MAVEN_API_BASE}/info-requests/public/${encodeURIComponent(requestCode)}`;

  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState('');
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [review, setReview] = useState({ decision: '', comment: '' });
  const [missing, setMissing] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const t = T[data?.language === 'fr' ? 'fr' : 'en'];

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(url);
        const d = r.data.data;
        setData(d);
        setAnswers(Object.fromEntries((d.questions || []).map(q => [q.id, q.answer || ''])));
        if (d.answered) setDone(true);
      } catch (e) {
        setFatal(e?.response?.data?.message || T.en.error);
      } finally { setLoading(false); }
    })();
  }, [url]);

  const submit = async () => {
    const req = (data.questions || []).filter(q => q.required && !String(answers[q.id] || '').trim()).map(q => q.id);
    const reviewMissing = data.reviewResume && (!review.decision || (review.decision === 'changes_requested' && !review.comment.trim()));
    setMissing(req);
    if (req.length || reviewMissing) { setError(t.missing); return; }
    const anyAnswer = (data.questions || []).some(q => String(answers[q.id] || '').trim());
    if ((data.questions || []).length && !data.reviewResume && !anyAnswer) { setError(t.atLeastOne); return; }
    setSending(true); setError('');
    try {
      await axios.post(`${url}/submit`, { answers, review: data.reviewResume ? review : undefined });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      const d = e?.response?.data || {};
      if (d.code === 'ANSWERED') { setDone(true); return; }
      if (d.code === 'MISSING') { setMissing(d.missing || []); setError(t.missing); return; }
      if (d.code === 'EMPTY') { setError(t.atLeastOne); return; }
      if (e?.response?.status === 410) { setFatal(d.message); return; }
      setError(d.message || t.error);
    } finally { setSending(false); }
  };

  if (loading) return <Center><LogoLoader size={170} message={T.en.loading} /></Center>;
  if (fatal) return <Center><div style={{ fontSize: 40 }}>🔗</div><Title>{fatal}</Title></Center>;
  if (done) {
    return (
      <Center>
        <div style={{ fontSize: 48 }}>✅</div>
        <Title>{data?.answered && !sending ? t.already : t.doneTitle}</Title>
        {!data?.answered && <Muted>{t.doneText}</Muted>}
      </Center>
    );
  }

  const field = (q) => {
    const v = answers[q.id] || '';
    const set = (x) => setAnswers(a => ({ ...a, [q.id]: x }));
    switch (q.type) {
      case 'textarea': return <Area id={q.id} value={v} onChange={e => set(e.target.value)} />;
      case 'date': return <Input id={q.id} type="date" value={v} onChange={e => set(e.target.value)} />;
      case 'number': return <Input id={q.id} type="number" inputMode="decimal" value={v} onChange={e => set(e.target.value)} />;
      case 'yesno': return (
        <div style={{ display: 'flex', gap: 10 }}>
          <Choice type="button" $on={v === 'yes'} onClick={() => set('yes')}>{t.yes}</Choice>
          <Choice type="button" $on={v === 'no'} onClick={() => set('no')}>{t.no}</Choice>
        </div>
      );
      case 'select': return (
        <Sel id={q.id} value={v} onChange={e => set(e.target.value)}>
          <option value="">{t.choose}</option>
          {(q.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </Sel>
      );
      default: return <Input id={q.id} value={v} onChange={e => set(e.target.value)} />;
    }
  };

  const until = data.expiresAt ? new Date(data.expiresAt).toLocaleDateString(data.language === 'fr' ? 'fr-CA' : 'en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return (
    <Page>
      <Card>
        <Muted>{t.from} <b style={{ color: '#7955cd' }}>{data.expertName}</b></Muted>
        <Title style={{ marginTop: 6 }}>{data.title}</Title>
        {data.message && <Muted style={{ marginTop: 8 }}>{data.message}</Muted>}
        {until && <Muted style={{ marginTop: 8, fontSize: 12.5 }}>{t.validUntil} {until}.</Muted>}
      </Card>

      {(data.questions || []).length > 0 && (
        <Card>
          <Muted style={{ fontSize: 13 }}>{t.optional}</Muted>
          {data.questions.map((q, i) => (
            <Q key={q.id} $err={missing.includes(q.id)}>
              <QLabel htmlFor={q.id}>{i + 1}. {q.question}{q.required && <span style={{ color: '#c53030' }}> *</span>}</QLabel>
              {q.helpText && <Help>{q.helpText}</Help>}
              {field(q)}
            </Q>
          ))}
        </Card>
      )}

      {data.reviewResume && (
        <Card>
          <Title style={{ fontSize: 19 }}>{t.reviewTitle}</Title>
          <Muted>{t.reviewHelp}</Muted>
          <LinkBtn href={`${url}/resume`} target="_blank" rel="noopener noreferrer">📄 {t.open}</LinkBtn>
          <Frame title="resume" src={`${url}/resume`} />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Choice type="button" $on={review.decision === 'approved'} onClick={() => setReview(r => ({ ...r, decision: 'approved' }))}>✅ {t.approve}</Choice>
            <Choice type="button" $on={review.decision === 'changes_requested'} onClick={() => setReview(r => ({ ...r, decision: 'changes_requested' }))}>✏️ {t.changes}</Choice>
          </div>
          {review.decision === 'changes_requested' && (
            <Area value={review.comment} placeholder={t.changesPh} onChange={e => setReview(r => ({ ...r, comment: e.target.value }))} />
          )}
        </Card>
      )}

      {error && <ErrorText>{error}</ErrorText>}
      <Button onClick={submit} disabled={sending}>{sending ? t.sending : t.submit}</Button>
    </Page>
  );
}
